/**
 * 營運洞察 (/insights, boards/Insights.dc.html) — read_only+. Built only from licensing and
 * support records (the product is zero-telemetry): redemption trend with a cumulative
 * redemption-rate line, per-batch rates, the mint → redeem → revoke funnel, roster growth by
 * origin (GET /api/analytics/roster) and ticket status counts. 匯出 CSV is client-side from
 * the data on screen. The v1 alerts / metrics / audit sections moved out (audit → /audit).
 */
import { useMemo, useState } from 'react';
import { BarRateChart } from '@/components/charts';
import { Button } from '@/components/Button';
import { usePoll } from '@/lib/usePoll';
import { useAuth } from '@/contexts/AuthContext';
import { roleAtLeast } from '@/lib/roles';
import { minus } from '@/lib/format';
import { api, isApiError } from '@/lib/api';
import { buildCsv, downloadCsv } from '@/lib/csv';
import { dayToUtcMs } from '@/lib/dates';
import { fmtDay } from '@/lib/format';
import { TICKET_STATUS_LABEL, TICKET_STATUS_ORDER, pct } from '@/lib/labels';
import type { AnalyticsTimeline, AnalyticsTimelineParams, DashboardSummary, LicenseBatch, RosterAnalytics } from '@/types';

type RangeId = '7' | '30' | '90' | 'all' | 'custom';
const RANGES: { id: RangeId; label: string }[] = [
  { id: '7', label: '近 7 日' },
  { id: '30', label: '近 30 日' },
  { id: '90', label: '近 90 日' },
  { id: 'all', label: '全部' },
  { id: 'custom', label: '自訂…' },
];

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const forbidden = (e: unknown) => isApiError(e) && e.status === 403;

export function Insights() {
  const [range, setRange] = useState<RangeId>('30');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [batch, setBatch] = useState('');

  const customOk = range !== 'custom' || (dayToUtcMs(from) !== null && dayToUtcMs(to) !== null && from <= to);
  const params: AnalyticsTimelineParams =
    range === 'all' ? { all: true } : range === 'custom' ? { from, to } : { days: Number(range) };
  // Roster analytics takes a day count (1–365): mirror the chosen range.
  const rosterDays = useMemo(() => {
    if (range === 'all') return 365;
    if (range === 'custom') {
      const a = dayToUtcMs(from);
      const b = dayToUtcMs(to);
      return a !== null && b !== null && b >= a ? Math.min(365, Math.round((Date.now() - a) / 86_400_000) + 1) : 30;
    }
    return Number(range);
  }, [range, from, to]);

  const timeline = usePoll<AnalyticsTimeline>(
    () => (customOk ? api.analyticsTimeline(params) : Promise.reject(new Error('請選擇有效的日期範圍'))),
    60_000,
    [range, from, to],
  );
  const roster = usePoll<RosterAnalytics>(() => api.analyticsRoster(rosterDays), 60_000, [rosterDays]);
  const summary = usePoll<DashboardSummary>(() => api.dashboardSummary(), 30_000);
  // Per-batch revoked counts (incl. revoked after redemption) come from the batches list.
  // Admin-only endpoint: lower roles would only collect 403s (the funnel falls back to the timeline).
  const { role } = useAuth();
  const isAdmin = roleAtLeast(role, 'admin');
  const licBatches = usePoll<LicenseBatch[]>(() => (isAdmin ? api.listLicenseBatches() : Promise.resolve([])), 120_000, [isAdmin]);

  const t = timeline.data;
  const batchStat = t?.by_batch.find((b) => b.batch_id === batch) ?? null;
  const rangeLabel = range === 'custom' ? (from && to ? `${from} – ${to}` : '自訂') : RANGES.find((r) => r.id === range)?.label ?? '';

  // Daily bars + cumulative rate: the base is everything redeemed BEFORE the window
  // (lifetime total minus what the window shows), so the line ends at today's real rate.
  const chartData = useMemo(() => {
    if (!t) return [];
    const daily = batchStat
      ? t.days.map((d) => ({ date: d, activated: batchStat.daily.find((x) => x.date === d)?.activated ?? 0 }))
      : t.overall.map((p) => ({ date: p.date, activated: p.activated }));
    const inWindow = daily.reduce((n, d) => n + d.activated, 0);
    const lifetime = batchStat ? batchStat.activated_total : t.summary.activated_total;
    const denom = batchStat ? batchStat.quantity : t.summary.minted_total;
    let cum = Math.max(0, lifetime - inWindow);
    return daily.map((d) => {
      cum += d.activated;
      return { ...d, rate: denom > 0 ? Math.round((cum / denom) * 1000) / 10 : 0 };
    });
  }, [t, batchStat]);

  const exportCsv = () => {
    if (!t) return;
    const ts = new Date().toISOString().slice(0, 10);
    const csv = batchStat
      ? buildCsv(['日期', '批次', '當日兌換', '累計兌換率(%)'], chartData.map((d) => [d.date, batchStat.batch_id, String(d.activated), String(d.rate)]))
      : buildCsv(
          ['日期', '鑄造', '兌換', '撤銷', '加入名單', '累計兌換率(%)'],
          t.overall.map((p, i) => [p.date, String(p.minted), String(p.activated), String(p.revoked), String(p.whitelist_added), String(chartData[i]?.rate ?? '')]),
        );
    downloadCsv(`insights-${batchStat ? batchStat.batch_id + '-' : ''}${ts}.csv`, csv);
  };

  const funnel = t
    ? batchStat
      ? { minted: batchStat.quantity, activated: batchStat.activated_total, revoked: licBatches.data?.find((b) => b.batch_id === batchStat.batch_id)?.revoked ?? Math.max(0, batchStat.quantity - batchStat.activated_total - batchStat.remaining) }
      : { minted: t.summary.minted_total, activated: t.summary.activated_total, revoked: t.summary.revoked_total }
    : null;

  const growth = useMemo(() => {
    const days = roster.data?.days ?? [];
    const sum = (k: 'added_serial' | 'added_import' | 'added_manual' | 'revoked' | 'expired') => days.reduce((n, d) => n + (d[k] ?? 0), 0);
    return { serial: sum('added_serial'), imp: sum('added_import'), manual: sum('added_manual'), revoked: sum('revoked'), expired: sum('expired') };
  }, [roster.data]);

  const ticketCounts = summary.data?.ticket_counts;

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <h1 className="v2-title">營運洞察</h1>
          <p className="v2-sub">序號兌換、名單增減、工單 · 資料來自授權與工單紀錄,App 不回傳使用資料</p>
        </div>
        <label className="v2-field">序號批次
          <select className="v2-select" value={batch} onChange={(e) => setBatch(e.target.value)}>
            <option value="">全部批次</option>
            {(t?.by_batch ?? []).map((b) => <option key={b.batch_id} value={b.batch_id}>{b.batch_id}</option>)}
          </select>
        </label>
        <div className="filter-chips" role="group" aria-label="期間">
          {RANGES.map((r) => (
            <button key={r.id} type="button" className={'filter-chip' + (range === r.id ? ' active' : '')} aria-pressed={range === r.id} onClick={() => setRange(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
        <Button className="btn-outline" onClick={exportCsv} disabled={!t}>匯出 CSV</Button>
      </div>

      {range === 'custom' && (
        <div className="filterbar">
          <div className="v2-field">自訂範圍
            <div className="date-range">
              <input className="v2-input" type="date" aria-label="開始日期" value={from} onChange={(e) => setFrom(e.target.value)} />
              <span>–</span>
              <input className="v2-input" type="date" aria-label="結束日期" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          {!customOk && <span className="muted-note">選好開始與結束日期後顯示</span>}
        </div>
      )}

      {forbidden(timeline.error) ? (
        <section className="panel"><span className="muted-note">你的角色沒有序號分析的權限。</span></section>
      ) : (
        <div className="split-3-2">
          <div className="stack-14">
            <section className="panel">
              <div className="panel-head">
                <h2 className="panel-title grow">序號兌換趨勢({rangeLabel}){batchStat ? ` · ${batchStat.batch_id}` : ''}</h2>
              </div>
              {t ? (
                <BarRateChart
                  data={chartData}
                  bar={{ key: 'activated', name: '每日兌換', color: 'var(--primary)' }}
                  rate={{ key: 'rate', name: '累計兌換率', color: 'var(--warning)' }}
                />
              ) : (
                <span className="muted-note">{Boolean(timeline.error) && customOk ? `載入失敗:${errText(timeline.error)}` : '載入中…'}</span>
              )}
            </section>

            <section className="panel flush">
              <div className="panel-head"><h2 className="panel-title grow">各批次兌換率</h2></div>
              <div className="tscroll">
                <table className="v2-table">
                  <thead>
                    <tr><th>批次</th><th>合作方</th><th>數量</th><th>已兌換</th><th>剩餘</th><th>兌換率</th><th>最後兌換</th></tr>
                  </thead>
                  <tbody>
                    {(t?.by_batch ?? []).length === 0 ? (
                      <tr className="empty-row"><td colSpan={7}>{t ? '尚無批次' : '載入中…'}</td></tr>
                    ) : (
                      (t?.by_batch ?? []).map((b) => {
                        const p = Math.round(b.activation_rate * 100);
                        return (
                          <tr key={b.batch_id} className={'clickable' + (b.batch_id === batch ? ' selected' : '')} onClick={() => setBatch(b.batch_id === batch ? '' : b.batch_id)}>
                            <td className="mono">{b.batch_id}</td>
                            <td>{b.partner || '—'}</td>
                            <td className="num">{b.quantity}</td>
                            <td className="num">{b.activated_total}</td>
                            <td className="num">{b.remaining}</td>
                            <td>
                              <div className="rate">
                                <span className="track"><span className="fill" style={{ width: `${p}%` }} /></span>
                                <span className="p">{p}%</span>
                              </div>
                            </td>
                            <td className="muted">{b.last_activated_at ? fmtDay(b.last_activated_at) : '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <div className="stack-14">
            <section className="panel" style={{ gap: 10 }}>
              <h2 className="panel-title">兌換漏斗{batchStat ? ` · ${batchStat.batch_id}` : ''}</h2>
              {funnel ? (
                [
                  { label: '已鑄造', n: funnel.minted, color: 'var(--text-subtle)' },
                  { label: '已兌換', n: funnel.activated, color: 'var(--primary)' },
                  { label: '已撤銷', n: funnel.revoked, color: 'var(--error)' },
                ].map((f) => (
                  <div key={f.label} className="funnel-row">
                    <span className="k">{f.label}</span>
                    <span className="track"><span className="fill" style={{ width: `${funnel.minted ? (f.n / funnel.minted) * 100 : 0}%`, background: f.color }} /></span>
                    <span className="n">{f.n}</span>
                  </div>
                ))
              ) : (
                <span className="muted-note">載入中…</span>
              )}
              {funnel && <span className="muted-note">兌換率 {pct(funnel.activated, funnel.minted)}%</span>}
            </section>

            {!forbidden(roster.error) && (
              <section className="panel" style={{ gap: 10 }}>
                <h2 className="panel-title">名單增減({range === 'all' ? '近 365 日' : rangeLabel})</h2>
                {roster.data ? (
                  <div className="tiles growth" style={{ gap: 10 }}>
                    <div className="tile plain">
                      <div className="tile-label">新增</div>
                      <div className="tile-value tone-success">+{growth.serial + growth.imp + growth.manual}</div>
                      <div className="tile-sub" title={`兌換 ${growth.serial} · 匯入 ${growth.imp}${growth.manual ? ` · 手動 ${growth.manual}` : ''}`}>
                        <span className="nowrap">兌換 {growth.serial}</span> · <span className="nowrap">匯入 {growth.imp}</span>
                        {growth.manual ? <> · <span className="nowrap">手動 {growth.manual}</span></> : null}
                      </div>
                    </div>
                    <div className="tile plain">
                      <div className="tile-label">撤銷</div>
                      <div className="tile-value tone-danger">{minus(growth.revoked)}</div>
                      <div className="tile-sub">後台撤銷</div>
                    </div>
                    <div className="tile plain">
                      <div className="tile-label">到期</div>
                      <div className="tile-value tone-warning">{minus(growth.expired)}</div>
                      <div className="tile-sub">到期日已過</div>
                    </div>
                  </div>
                ) : (
                  <span className="muted-note">{roster.error ? `載入失敗:${errText(roster.error)}` : '載入中…'}</span>
                )}
              </section>
            )}

            {ticketCounts && (
              <section className="panel" style={{ gap: 'var(--space-2)' }}>
                <h2 className="panel-title">工單狀態</h2>
                {TICKET_STATUS_ORDER.map((s) => (
                  <div key={s} className="row" style={{ justifyContent: 'space-between', fontSize: 'var(--text-small)' }}>
                    <span className="muted">{TICKET_STATUS_LABEL[s]}</span>
                    <span className="num" style={{ fontWeight: 'var(--weight-semibold)' }}>{ticketCounts[s] ?? 0}</span>
                  </div>
                ))}
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
